import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { inboundService } from '@services/operator/inboundService';
import { receiptService } from '@services/operator/receiptService';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import inventoryAttributeDefinitionService from '@services/operator/inventoryAttributeDefinitionService';
import merchantInventoryTypeService from '@services/operator/merchantInventoryTypeService';
import soundEffects from '@utils/soundEffects';
import { message, Modal } from 'antd';
import type { AttributeDefinition } from '@components/operator/shared/inventory_verification/types';

// 後端把商品自訂屬性必填的缺漏欄位用 "product.custom_attributes.{name}" 標記，
// 跟庫存自訂屬性的 "custom_attributes.{name}" 區分；前端據此導向商品主檔補填，
// 而不是繼續在點收欄位上掙扎。
const PRODUCT_ATTR_FIELD_PREFIX = 'product.custom_attributes.';

const extractMissingProductAttributeNames = (fields: unknown): string[] => {
  if (!Array.isArray(fields)) return [];
  return fields
    .filter((f): f is string => typeof f === 'string' && f.startsWith(PRODUCT_ATTR_FIELD_PREFIX))
    .map((f) => f.slice(PRODUCT_ATTR_FIELD_PREFIX.length));
};

interface ProductAttributeMissing {
  fields: string[];
}

interface InboundItem {
  id: string | null;
  product_id?: string | null;
  product_name?: string | null;
  product_sku?: string | null;
  product_barcode?: string | null;
  product_image_url?: string | null;
  product_validity_days?: number | null;
  batch_management_required?: boolean;
  manufacturing_date_required?: boolean;
  required_serial_numbers_count?: number | null;
  serial_number_pattern?: string | null;
  small_quantity_per_large_unit?: number | null;
  scheduled_quantity?: number;
  received_quantity?: number;
  inventory_type_id?: string | null;
  inventory_type_name?: string;
  custom_attributes?: Record<string, unknown> | null;
  [key: string]: unknown;
}

interface Receipt {
  id: string;
  status: string;
  total_items?: number;
  total_received_quantity?: number;
  [key: string]: unknown;
}

interface ActiveReceiptSummary {
  id: string;
  item_progress?: { scanned?: number; [key: string]: unknown };
  [key: string]: unknown;
}

interface Inbound {
  id: string;
  merchant_id?: string;
  // Merchant 級 PSN policy（per docs/guidelines + ProductSerialNumber skill）：
  // outbound_only 時驗收階段不可收 SN，FE 不應顯示序號輸入區。
  serial_tracking_policy?: 'outbound_only' | 'full_lifecycle' | null;
  items?: InboundItem[];
  receipts?: Receipt[];
  active_receipt?: ActiveReceiptSummary | null;
  [key: string]: unknown;
}

interface Product {
  id: string;
  name?: string | null;
  sku?: string | null;
  barcode?: string | null;
  public_photo_url?: string | null;
  image_url?: string | null;
  validity_days?: number | null;
  batch_management_required?: boolean;
  manufacturing_date_required?: boolean;
  required_serial_numbers_count?: number | null;
  serial_number_pattern?: string | null;
  small_quantity_per_large_unit?: number | null;
  [key: string]: unknown;
}

interface InventoryType {
  inventory_type_id: string;
  inventory_type?: { name?: string; full_name?: string };
  [key: string]: unknown;
}

interface ReceiptItem {
  id: string;
  [key: string]: unknown;
}

interface VerifyAdditionalData {
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  inventory_type_id?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  receipt_item_id?: string | null;
  received_box_count?: number | null;
  received_quantity_per_box?: number | null;
  received_loose_quantity?: number | null;
  serial_numbers?: string[];
  note?: string | null;
}

interface CreateItemData {
  product_id?: string;
  received_quantity?: number;
  [key: string]: unknown;
}

// 補填商品必填屬性後 retry 用：必須區分 verify / create_unplanned 兩個 endpoint，
// 不能讓 unplanned create 失敗後沿用上一筆 verify 的 inboundItemId 重打 batch_receive
// （會誤收到錯誤品項）。
type LastFailedAction =
  | { kind: 'verify'; args: { inboundItemId: string; quantity: number; additionalData: VerifyAdditionalData } }
  | { kind: 'create'; args: CreateItemData };

interface ScanResult {
  success: boolean;
  item?: InboundItem | Product;
  multiple?: boolean;
}

interface InboundAcceptanceValue {
  inbound: Inbound | null;
  loading: boolean;
  error: string | null;
  scannedItem: InboundItem | null;
  setScannedItem: React.Dispatch<React.SetStateAction<InboundItem | null>>;
  editingReceiptItem: ReceiptItem | null;
  setEditingReceiptItem: React.Dispatch<React.SetStateAction<ReceiptItem | null>>;
  scanBarcode: (code: string) => Promise<ScanResult | undefined>;
  verifyItem: (inboundItemId: string, quantity: number, additionalData?: VerifyAdditionalData) => Promise<boolean>;
  createItem: (data: CreateItemData) => Promise<boolean>;
  deleteReceiptItem: (receiptItemId: string) => Promise<boolean>;
  submitting: boolean;
  refresh: () => Promise<Inbound | null>;
  attributeDefinitions: AttributeDefinition[];
  productAttributeDefinitions: AttributeDefinition[];
  productAttributeMissing: ProductAttributeMissing | null;
  clearProductAttributeMissing: () => void;
  retryLastAction: () => Promise<boolean>;
  inventoryTypes: InventoryType[];
  productCandidates: Product[] | null;
  selectProduct: (product: Product) => Promise<boolean>;
  setProductCandidates: React.Dispatch<React.SetStateAction<Product[] | null>>;
  activeReceipt: Receipt | null;
  receiptLoading: boolean;
  finalizeReceipt: (closeInbound?: boolean) => Promise<void>;
  closeInbound: () => Promise<void>;
  receiptSelectorOpen: boolean;
  activeReceiptSummary: ActiveReceiptSummary | null;
  handleResumeReceipt: () => void;
  handleStartNewReceipt: () => Promise<void>;
  handleCancelReceiptSelector: () => void;
  drawerOpen: boolean;
  openVerification: (item: InboundItem) => Promise<void>;
  closeVerification: () => void;
}

interface InboundAcceptanceProviderProps {
  children: React.ReactNode;
  initialData?: Inbound | null;
}

const InboundAcceptanceContext = createContext<InboundAcceptanceValue | undefined>(undefined);

export const useInboundAcceptance = (): InboundAcceptanceValue =>
  useContext(InboundAcceptanceContext) as InboundAcceptanceValue;

export const InboundAcceptanceProvider: React.FC<InboundAcceptanceProviderProps> = ({ children, initialData }) => {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const productService = useOperatorProductService();
  const [inbound, setInbound] = useState<Inbound | null>(initialData || null);
  const [loading, setLoading] = useState<boolean>(!initialData);
  const [error, setError] = useState<string | null>(null);
  const [scannedItem, setScannedItem] = useState<InboundItem | null>(null);
  const [editingReceiptItem, setEditingReceiptItem] = useState<ReceiptItem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [productCandidates, setProductCandidates] = useState<Product[] | null>(null);
  const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinition[]>([]);
  const [productAttributeDefinitions, setProductAttributeDefinitions] = useState<AttributeDefinition[]>([]);
  const [inventoryTypes, setInventoryTypes] = useState<InventoryType[]>([]);

  // 商品必填屬性缺漏：當 verifyItem / createItem 收到 422 + product.custom_attributes.* 時填入
  // 缺漏欄位名稱陣列，VerificationDrawer 會跳 modal 引導開 ProductQuickEditDrawer 補填，補完 retry。
  const [productAttributeMissing, setProductAttributeMissing] = useState<ProductAttributeMissing | null>(null);
  // 補填後重新送出時要用的「上一個失敗的 action 與參數」。用 ref 避免每次更新都觸發 re-render。
  const lastFailedActionRef = useRef<LastFailedAction | null>(null);

  // Receipt state
  const [activeReceipt, setActiveReceipt] = useState<Receipt | null>(null);
  const [receiptLoading, setReceiptLoading] = useState<boolean>(false);

  // Resume-or-new selector modal state. Driven by `inbound.active_receipt`
  // from the BE (see `Api::Operator::V1::InboundsController#show`).
  const [receiptSelectorOpen, setReceiptSelectorOpen] = useState<boolean>(false);
  const [activeReceiptSummary, setActiveReceiptSummary] = useState<ActiveReceiptSummary | null>(null);

  // Per-session ref so we only prompt once for the same active receipt id.
  // Without this, every refetch (e.g. after `verifyItem`) would re-open the
  // modal even while the operator is mid-verification.
  const promptedReceiptIdRef = useRef<string | null>(null);

  // Auto-complete modal flag to prevent duplicate modals
  const completionModalShownRef = useRef<boolean>(false);

  // Re-entrance guard for drawer actions (verify / create / delete). Shared
  // because the drawer only ever exposes one of these at a time. Ref handles
  // race-proof rejection of fast double-taps; state drives the panel's
  // loading spinner so the operator can see something is happening.
  const submittingRef = useRef<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const fetchAttributeDefinitions = useCallback(async (merchantId: string) => {
    if (!merchantId) return;
    try {
      const response = await inventoryAttributeDefinitionService.fetchList({ merchant_id: merchantId });
      if (response.success) {
        // Cast: BE returns data_type as plain string, shared type narrows to a
        // literal union. Trust BE to only send valid values.
        setAttributeDefinitions(response.data as AttributeDefinition[]);
      }
    } catch (err) {
      console.error('Failed to fetch attribute definitions:', err);
    }
  }, []);

  const fetchProductAttributeDefinitions = useCallback(async (merchantId: string) => {
    if (!merchantId) return;
    try {
      // productService schema 是 `{ data: unknown[] }`（非 `{ success, data }`），
      // 與 inventoryAttributeDefinitionService 不同，不要混用判斷。
      const response = await productService.getProductAttributeDefinitions(merchantId);
      if (Array.isArray(response?.data)) {
        // productService schema return `{ data: unknown[] }`，跨 boundary cast 到本 context 用的 record 型別。
        setProductAttributeDefinitions(response.data as AttributeDefinition[]);
      }
    } catch (err) {
      // 定義載入失敗不阻擋驗收流程，缺漏 modal 會 fallback 顯示 raw name；
      // 故意不丟 toast 避免讓使用者誤以為操作出錯。
      console.error('Failed to fetch product attribute definitions:', err);
    }
  }, [productService]);

  const fetchInventoryTypes = useCallback(async (merchantId: string) => {
    if (!merchantId) return;
    try {
      const response = await merchantInventoryTypeService.fetchList({ merchantId });
      if (response.success) {
        setInventoryTypes(response.data);
      } else if (Array.isArray(response)) {
        setInventoryTypes(response);
      }
    } catch (err) {
      console.error('Failed to fetch inventory types:', err);
    }
  }, []);

  // Decide whether to surface the resume-or-new selector modal for this load.
  // Trigger condition: BE returned an `active_receipt` summary AND it has
  // scanned items (scanned > 0). An empty active receipt is silently resumed
  // — no value in interrupting the operator for a fresh, untouched batch.
  // We also gate on `promptedReceiptIdRef` so refetches mid-verification
  // (e.g. after `verifyItem` → `fetchInbound`) don't pop the modal again.
  const maybeOpenReceiptSelector = useCallback((data: Inbound | null | undefined) => {
    const summary = data?.active_receipt || null;
    setActiveReceiptSummary(summary);
    if (!summary) {
      promptedReceiptIdRef.current = null;
      return;
    }
    if (promptedReceiptIdRef.current === summary.id) return;
    promptedReceiptIdRef.current = summary.id;
    const scanned = summary.item_progress?.scanned ?? 0;
    if (scanned <= 0) return;
    setReceiptSelectorOpen(true);
  }, []);

  const fetchInbound = useCallback(async (): Promise<Inbound | null> => {
    if (!inbound) setLoading(true);

    try {
      const response = await inboundService.get(id);
      if (response.success) {
        const data = response.data as Inbound;
        setInbound(data);

        // Derive active receipt from inbound.receipts
        const receiving = data.receipts?.find((r: Receipt) => r.status === 'receiving');
        setActiveReceipt(receiving || null);
        maybeOpenReceiptSelector(data);
        return data;
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
    return null;
  }, [id, maybeOpenReceiptSelector]);

  useEffect(() => {
    if (initialData) {
      const receiving = initialData.receipts?.find((r: Receipt) => r.status === 'receiving');
      setActiveReceipt(receiving || null);
      maybeOpenReceiptSelector(initialData);
    } else {
      fetchInbound();
    }
  }, [fetchInbound, initialData, maybeOpenReceiptSelector]);

  useEffect(() => {
    if (inbound && inbound.merchant_id) {
      fetchAttributeDefinitions(inbound.merchant_id);
      fetchProductAttributeDefinitions(inbound.merchant_id);
      fetchInventoryTypes(inbound.merchant_id);
    }
  }, [inbound, fetchAttributeDefinitions, fetchProductAttributeDefinitions, fetchInventoryTypes]);

  // Receipt management. Receipt creation is lazy — triggered on first
  // verification action (see `openVerification`). No success toast: from the
  // operator's POV, scanning the first item just opens the drawer; the receipt
  // creation is implementation detail.
  const startNewReceipt = useCallback(async (): Promise<Receipt | null> => {
    if (!inbound) return null;
    setReceiptLoading(true);
    try {
      const response = await receiptService.create(inbound.id);
      if (response.success) {
        setActiveReceipt(response.data);
        await fetchInbound();
        return response.data;
      }
      message.error(response.message || (t('common.error_label') as string));
      return null;
    } catch (err) {
      message.error((err as Error).message);
      return null;
    } finally {
      setReceiptLoading(false);
    }
  }, [inbound, t, fetchInbound]);

  // User chose "繼續驗收" — there's nothing to do server-side, the existing
  // :receiving receipt is already loaded into state by `fetchInbound`. We
  // just dismiss the modal.
  const handleResumeReceipt = useCallback(() => {
    setReceiptSelectorOpen(false);
  }, []);

  // User chose "開新一張" — call BE which (1) finalizes current :receiving
  // receipt to :pending_putaway, (2) creates a fresh empty :receiving
  // receipt, then refresh local state.
  const handleStartNewReceipt = useCallback(async () => {
    if (!inbound) return;
    setReceiptLoading(true);
    try {
      const response = await receiptService.startNewInboundReceipt(inbound.id);
      if (response.success) {
        // Clear the prompt-gate so we don't immediately re-trigger; new
        // receipt starts at scanned = 0 anyway, but be defensive.
        promptedReceiptIdRef.current = response.data?.id || null;
        setReceiptSelectorOpen(false);
        message.success(t('operator.inbound_acceptance.receipt_selector.start_new_success', {
          defaultValue: '已開新一張驗收單'
        }));
        await fetchInbound();
      } else {
        // BE carries the i18n key in `interpolations.message` (e.g.
        // "cannot_start_new_receipt"); `message` itself is the raw error code
        // ("INVALID_OPERATION") and must never be shown to the operator.
        const errorKey = response.interpolations?.message as string | undefined;
        const fallback = t('common.error.operation_failed') as string;
        message.error(
          errorKey
            ? (t(`operator.inbound_acceptance.errors.${errorKey}`, { defaultValue: fallback }) as string)
            : fallback
        );
      }
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setReceiptLoading(false);
    }
  }, [inbound, t, fetchInbound]);

  const handleCancelReceiptSelector = useCallback(() => {
    setReceiptSelectorOpen(false);
  }, []);

  const finalizeReceipt = useCallback(async (closeInbound: boolean = false) => {
    if (!activeReceipt) return;
    setReceiptLoading(true);
    try {
      const response = await receiptService.finalize(activeReceipt.id, { closeInbound });
      if (response.success) {
        setActiveReceipt(null);
        const msg = closeInbound
          ? t('operator.receipt.inbound_closed', { defaultValue: '入庫單已結案' })
          : t('operator.receipt.finalized', { defaultValue: '此批驗收已結案' });
        message.success(msg);
        await fetchInbound();
      } else {
        message.error(response.message || (t('common.error_label') as string));
      }
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setReceiptLoading(false);
    }
  }, [activeReceipt, t, fetchInbound]);

  const closeInbound = useCallback(async () => {
    setReceiptLoading(true);
    try {
      const response = await inboundService.closeReceiving(id);
      if (response.success) {
        setActiveReceipt(null);
        message.success(t('operator.receipt.inbound_closed', { defaultValue: '入庫單已結案' }));
        await fetchInbound();
      } else {
        message.error(response.message || (t('common.error_label') as string));
      }
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setReceiptLoading(false);
    }
  }, [id, t, fetchInbound]);

  const openVerification = useCallback(async (item: InboundItem) => {
    if (!activeReceipt) {
      const created = await startNewReceipt();
      if (!created) return;
    }
    setScannedItem(item);
    setDrawerOpen(true);
  }, [activeReceipt, startNewReceipt]);

  const closeVerification = useCallback(() => {
    setDrawerOpen(false);
    setEditingReceiptItem(null);
    // 同步清掉 product-attr 缺漏 state：操作者直接點 Drawer X 關閉時，下一個品項
    // 一打開 VerificationDrawer 就會因 `open={!!productAttributeMissing}` 立刻
    // 為上一個品項的缺漏欄位跳出 modal。
    setProductAttributeMissing(null);
    lastFailedActionRef.current = null;
    // Delay clearing scannedItem so Drawer close animation finishes
    setTimeout(() => setScannedItem(null), 300);
  }, []);

  const selectProduct = useCallback(async (product: Product): Promise<boolean> => {
    try {
      const detailRes = await productService.get(product.id);
      const fullProduct: Product = detailRes.success ? detailRes.data : product;

      soundEffects.playScan();
      setProductCandidates(null);

      Modal.confirm({
        title: t('operator.inbound_item.product_not_in_inbound', { defaultValue: '商品不在此入庫單中' }),
        content: t('operator.inbound_item.confirm_add_product', {
          defaultValue: `找到商品 ${fullProduct.name} (${fullProduct.sku})，是否新增並驗收？`,
          name: fullProduct.name,
          sku: fullProduct.sku
        }),
        okText: t('operator.inbound_item.add_and_verify', { defaultValue: '新增並驗收' }),
        cancelText: t('common.button.cancel', { defaultValue: '取消' }),
        onOk: () => {
          const virtualItem: InboundItem = {
            id: null,
            product_id: fullProduct.id,
            product_name: fullProduct.name,
            product_sku: fullProduct.sku,
            product_barcode: fullProduct.barcode,
            product_image_url: fullProduct.public_photo_url || fullProduct.image_url,
            product_validity_days: fullProduct.validity_days,
            batch_management_required: fullProduct.batch_management_required,
            manufacturing_date_required: fullProduct.manufacturing_date_required,
            required_serial_numbers_count: fullProduct.required_serial_numbers_count,
            serial_number_pattern: fullProduct.serial_number_pattern,
            small_quantity_per_large_unit: fullProduct.small_quantity_per_large_unit,
            scheduled_quantity: 0,
            received_quantity: 0,
            inventory_type_id: null,
            inventory_type_name: t('common.unspecified', { defaultValue: '未指定' }) as string,
            custom_attributes: {}
          };
          openVerification(virtualItem);
        }
      });
      return true;
    } catch (err) {
      console.error("Select product failed", err);
      message.error(t('common.operation_failed', { defaultValue: '操作失敗' }));
      return false;
    }
  }, [t, openVerification]);

  const scanBarcode = useCallback(async (code: string): Promise<ScanResult | undefined> => {
    if (!inbound || !inbound.items) return;

    const trimmedCode = code.trim();

    const matchedItem = inbound.items.find((item: InboundItem) =>
      (item.product_barcode === trimmedCode) ||
      (item.product_sku === trimmedCode)
    );

    if (matchedItem) {
      soundEffects.playScan();
      openVerification(matchedItem);
      return { success: true, item: matchedItem };
    } else {
      try {
        const response = await productService.fetchList({
          sku_or_barcode: trimmedCode,
          merchant_id: inbound.merchant_id,
          page_size: 10,
          include_photo: true
        });

        if (response.success && response.data && response.data.length > 0) {
          const products: Product[] = response.data;

          if (products.length === 1) {
            await selectProduct(products[0]);
            return { success: true, item: products[0] };
          } else {
            soundEffects.playScan();
            setProductCandidates(products);
            return { success: true, multiple: true };
          }
        }
      } catch (err) {
        console.error("Product search failed", err);
      }

      soundEffects.playBuzzerError();
      message.error(t('operator.scan_not_found', { defaultValue: '找不到條碼: {{code}}', code: trimmedCode }) as string);
      return { success: false };
    }
  }, [inbound, id, selectProduct, openVerification, t]);

  // Helper function to translate interpolation values
  const translateInterpolations = useCallback((interpolations: Record<string, unknown> | null | undefined): Record<string, unknown> => {
    if (!interpolations) return {};
    const translated: Record<string, unknown> = { ...interpolations };
    if (translated.mode) {
      const modeKey = `enums.merchant.storage_billing_mode.${translated.mode}`;
      const translatedMode = t(modeKey, { defaultValue: translated.mode as string });
      translated.mode = translatedMode;
    }
    return translated;
  }, [t]);

  // Check if all planned inbound items are fully verified and show completion modal
  const checkAndShowCompletionModal = useCallback((updatedInbound: Inbound) => {
    if (completionModalShownRef.current) return;

    // Derive fresh receipt from the just-fetched inbound data (avoids stale closure)
    const freshReceipt = updatedInbound.receipts?.find((r: Receipt) => r.status === 'receiving');
    if (!freshReceipt) return;

    const plannedItems = updatedInbound.items?.filter((item: InboundItem) => (item.scheduled_quantity ?? 0) > 0) || [];
    if (plannedItems.length === 0) return;

    const allVerified = plannedItems.every((item: InboundItem) => (item.received_quantity ?? 0) >= (item.scheduled_quantity ?? 0));
    if (!allVerified) return;

    completionModalShownRef.current = true;

    Modal.confirm({
      title: t('operator.receipt.all_items_verified_title', { defaultValue: '本批驗收已全部完成' }),
      content: t('operator.receipt.all_items_verified_content', {
        defaultValue: '已驗收 {{items}} 項商品，共 {{qty}} 件',
        items: freshReceipt.total_items || 0,
        qty: freshReceipt.total_received_quantity || 0
      }),
      okText: t('operator.receipt.finalize_this', { defaultValue: '結案此批驗收' }),
      cancelText: t('operator.receipt.continue_receiving', { defaultValue: '繼續驗收' }),
      autoFocusButton: 'cancel',
      onOk: () => {
        completionModalShownRef.current = false;
        finalizeReceipt(false);
      },
      onCancel: () => {
        completionModalShownRef.current = false;
      }
    });
  }, [t, finalizeReceipt]);

  // Verify item via receipt_items/batch_receive (receipt-centric)
  const verifyItem = useCallback(async (inboundItemId: string, quantity: number, additionalData: VerifyAdditionalData = {}): Promise<boolean> => {
    if (submittingRef.current) return false;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const item = {
        id: inboundItemId,
        received_quantity: quantity,
        batch: additionalData.batch,
        expiration_date: additionalData.expiration_date,
        manufacturing_date: additionalData.manufacturing_date,
        inventory_type_id: additionalData.inventory_type_id,
        custom_attributes: additionalData.custom_attributes,
        receipt_item_id: additionalData.receipt_item_id,
        received_box_count: additionalData.received_box_count,
        received_quantity_per_box: additionalData.received_quantity_per_box,
        received_loose_quantity: additionalData.received_loose_quantity,
        serial_numbers: additionalData.serial_numbers,
        note: additionalData.note
      };

      const response = await receiptService.batchReceive([item]);

      if (response.success) {
        const result = response.data?.results?.[0];
        if (result?.success) {
          soundEffects.playScan();
          message.success(t('operator.verify_success', { defaultValue: '驗收成功' }));
          closeVerification();
          const refreshed = await fetchInbound();
          if (refreshed) checkAndShowCompletionModal(refreshed);
          return true;
        } else {
          // 商品必填屬性缺漏：交給 VerificationDrawer 跳 modal + ProductQuickEditDrawer 補填，
          // 不丟 toast 避免雙重訊息（modal + toast 同時出現會讓使用者搞不清楚要看哪個）。
          const productAttrFields = extractMissingProductAttributeNames(
            (result?.interpolations as Record<string, unknown> | undefined)?.fields
          );
          if (productAttrFields.length > 0) {
            soundEffects.playBuzzerError();
            lastFailedActionRef.current = {
              kind: 'verify',
              args: { inboundItemId, quantity, additionalData },
            };
            setProductAttributeMissing({ fields: productAttrFields });
            return false;
          }

          soundEffects.playBuzzerError();
          const msgKey = `operator.inbound_item.errors.${result?.message}`;
          const translatedInterpolations = translateInterpolations(result?.interpolations);
          const msg = t(msgKey, { ...translatedInterpolations, defaultValue: result?.message || t('operator.verify_failed', { defaultValue: '驗收失敗' }) });
          message.error(msg as string);
          return false;
        }
      } else {
        soundEffects.playBuzzerError();
        message.error(response.message || (t('operator.verify_failed', { defaultValue: '驗收失敗' }) as string));
        return false;
      }
    } catch (err) {
      soundEffects.playBuzzerError();
      message.error((err as Error).message);
      return false;
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [t, translateInterpolations, fetchInbound, checkAndShowCompletionModal, closeVerification]);

  const clearProductAttributeMissing = useCallback(() => {
    setProductAttributeMissing(null);
    lastFailedActionRef.current = null;
  }, []);

  // Create unplanned item via receipt_items/create_unplanned
  const createItem = useCallback(async (data: CreateItemData): Promise<boolean> => {
    if (submittingRef.current) return false;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const response = await receiptService.createUnplanned({
        inbound_id: id,
        ...data
      });
      if (response.success) {
        soundEffects.playScan();
        message.success(t('operator.create_and_verify_success', { defaultValue: '新增並驗收成功' }));
        closeVerification();
        const refreshed = await fetchInbound();
        if (refreshed) checkAndShowCompletionModal(refreshed);
        return true;
      } else {
        // 商品必填屬性缺漏：與 verifyItem 一致，跳 ProductQuickEditDrawer 補商品主檔，
        // 不丟 toast 以免雙重訊息（modal + toast 同時出現會讓使用者搞不清楚要看哪個）。
        const productAttrFields = extractMissingProductAttributeNames(response.interpolations?.fields);
        if (productAttrFields.length > 0) {
          soundEffects.playBuzzerError();
          lastFailedActionRef.current = { kind: 'create', args: data };
          setProductAttributeMissing({ fields: productAttrFields });
          return false;
        }

        soundEffects.playBuzzerError();
        const msgKey = `operator.inbound_item.errors.${response.message}`;
        const translatedInterpolations = translateInterpolations(response.interpolations);
        const msg = t(msgKey, { ...translatedInterpolations, defaultValue: response.message || t('common.create_failed', { defaultValue: '新增失敗' }) });
        message.error(msg as string);
        return false;
      }
    } catch (err) {
      soundEffects.playBuzzerError();
      message.error((err as Error).message);
      return false;
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [id, t, translateInterpolations, fetchInbound, checkAndShowCompletionModal, closeVerification]);

  // 補填商品必填屬性後，沿用上一次失敗 action（verify / create_unplanned）的參數重新送出。
  // 必須依 kind 分派，否則 unplanned create 後使用者可能拿到上一筆 verify 的 inboundItemId
  // 反向打 batch_receive，誤收到錯誤品項。
  const retryLastAction = useCallback(async (): Promise<boolean> => {
    const last = lastFailedActionRef.current;
    if (!last) {
      console.error('retryLastAction called without a prior failed action');
      return false;
    }
    setProductAttributeMissing(null);
    if (last.kind === 'verify') {
      return verifyItem(last.args.inboundItemId, last.args.quantity, last.args.additionalData);
    }
    return createItem(last.args);
  }, [verifyItem, createItem]);

  // Delete receipt item via receipt_items/:id
  const deleteReceiptItem = useCallback(async (receiptItemId: string): Promise<boolean> => {
    if (submittingRef.current) return false;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const response = await receiptService.deleteReceiptItem(receiptItemId);
      if (response.success) {
        soundEffects.playScan();
        message.success(t('common.delete_success', { defaultValue: '刪除成功' }));
        closeVerification();
        await fetchInbound();
        return true;
      } else {
        soundEffects.playBuzzerError();
        const msgKey = `operator.inbound_item.errors.${response.message}`;
        const translatedInterpolations = translateInterpolations(response.interpolations);
        const msg = t(msgKey, { ...translatedInterpolations, defaultValue: response.message || t('common.delete_failed', { defaultValue: '刪除失敗' }) });
        message.error(msg as string);
        return false;
      }
    } catch (err) {
      soundEffects.playBuzzerError();
      message.error((err as Error).message);
      return false;
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [t, translateInterpolations, fetchInbound, closeVerification]);

  const value: InboundAcceptanceValue = {
    inbound,
    loading,
    error,
    scannedItem,
    setScannedItem,
    editingReceiptItem,
    setEditingReceiptItem,
    scanBarcode,
    verifyItem,
    createItem,
    deleteReceiptItem,
    submitting,
    refresh: fetchInbound,
    attributeDefinitions,
    productAttributeDefinitions,
    productAttributeMissing,
    clearProductAttributeMissing,
    retryLastAction,
    inventoryTypes,
    productCandidates,
    selectProduct,
    setProductCandidates,
    // Receipt management
    activeReceipt,
    receiptLoading,
    finalizeReceipt,
    closeInbound,
    // Resume-or-new selector
    receiptSelectorOpen,
    activeReceiptSummary,
    handleResumeReceipt,
    handleStartNewReceipt,
    handleCancelReceiptSelector,
    // Drawer state
    drawerOpen,
    openVerification,
    closeVerification,
  };

  return (
    <InboundAcceptanceContext.Provider value={value}>
      {children}
    </InboundAcceptanceContext.Provider>
  );
};
